import { makeAutoObservable, reaction } from 'mobx'

import {
  KyrgyzstanSearchApi,
  type RealtyObject,
  type RealtySearchRequest,
} from 'shared/api/kyrgyzstan/search'
import { getApiErrorMessage } from 'shared/lib/errorText'

export const BestOffersPageSize = 25

class KyrgyzstanBestOffersStore {
  offers: RealtyObject[] = []
  isLoading = false
  error: string | null = null
  abortController: AbortController | null = null

  page = 1
  pageSize = BestOffersPageSize
  hasMore = false
  totalCount = 0

  realtyTypeId?: number
  private isInitialized = false

  constructor() {
    makeAutoObservable(this)

    reaction(
      () => this.realtyTypeId,
      () => {
        if (this.isInitialized && this.offers.length > 0) {
          this.fetchFavorites({
            page: 1,
            page_size: this.pageSize,
            realty_type_id:
              this.realtyTypeId !== undefined
                ? String(this.realtyTypeId)
                : undefined,
          })
        }
      },
    )
  }

  setLoading = (loading: boolean) => {
    this.isLoading = loading
  }

  setError = (error: string | null) => {
    this.error = error
  }

  setOffers = (offers: RealtyObject[]) => {
    this.offers = offers
  }

  appendOffers = (offers: RealtyObject[]) => {
    this.offers = [...this.offers, ...offers]
  }

  setPage = (page: number) => {
    this.page = page
  }

  setHasMore = (hasMore: boolean) => {
    this.hasMore = hasMore
  }

  setTotalCount = (count: number) => {
    this.totalCount = count
  }

  setRealtyTypeId = (realtyTypeId?: number) => {
    this.realtyTypeId = realtyTypeId
  }

  fetchFavorites = async (params?: Partial<RealtySearchRequest>) => {
    this.setLoading(true)
    this.setError(null)

    try {
      const requestPage = params?.page || this.page
      const isNewSearch = requestPage === 1

      if (isNewSearch) {
        this.isInitialized = true
      }

      const requestParams: RealtySearchRequest = {
        page: requestPage,
        page_size: params?.page_size || this.pageSize,
        sort: params?.sort || null,
        realty_type_id: params?.realty_type_id,
      }

      if (isNewSearch && this.abortController) {
        this.abortController.abort()
      }

      const abortController = new AbortController()

      if (isNewSearch) {
        this.abortController = abortController
      }

      const { data: response } = await KyrgyzstanSearchApi.getFavorites(
        requestParams,
        abortController.signal,
      )

      if (isNewSearch) {
        this.setOffers(response.data)
      } else {
        this.appendOffers(response.data)
      }

      this.setPage(response.page)
      this.setHasMore(response.page < response.total_pages)
      this.setTotalCount(response.total_items)
    } catch (error) {
      const errorMessage = getApiErrorMessage(error)

      if (errorMessage === null) {
        return
      }

      this.setError(errorMessage)
      if (params?.page === 1 || !params?.page) {
        this.setOffers([])
      }
    } finally {
      this.setLoading(false)
    }
  }

  loadMore = async () => {
    if (this.isLoading || !this.hasMore) {
      return
    }

    const nextPage = this.page + 1
    await this.fetchFavorites({ page: nextPage })
  }

  reset = () => {
    this.offers = []
    this.page = 1
    this.isLoading = false
    this.error = null
    this.hasMore = false
    this.totalCount = 0
    this.realtyTypeId = undefined
    this.isInitialized = false

    if (this.abortController) {
      this.abortController.abort()
    }
  }
}

export const kyrgyzstanBestOffersStore = new KyrgyzstanBestOffersStore()
